// «Активно открытый чат» + фокус вкладки (SPEC §4.7 — граница владения: активность вкладки = состояние
// app-ui, подавление unread/тоста = вход стора через openChat/closeChat). ConversationScreen ставит
// активный чат при монтировании; вотчер in-app уведомлений читает его, чтобы не дребезжать по открытому.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { ChatId } from '@tele-chat/shared-contracts'

export interface ActiveChatValue {
  activeChatId: ChatId | null
  setActiveChatId(id: ChatId | null): void
  /** Вкладка в фокусе (document.visibilityState/hasFocus). */
  windowFocused: boolean
}

const ActiveChatContext = createContext<ActiveChatValue | null>(null)

export interface ActiveChatProviderProps {
  children: ReactNode
}

export function ActiveChatProvider({ children }: ActiveChatProviderProps): JSX.Element {
  const [activeChatId, setActiveChatId] = useState<ChatId | null>(null)
  const [windowFocused, setWindowFocused] = useState<boolean>(() =>
    typeof document === 'undefined' ? true : document.visibilityState === 'visible',
  )

  useEffect(() => {
    if (typeof document === 'undefined') return
    const onVisibility = (): void => setWindowFocused(document.visibilityState === 'visible')
    const onFocus = (): void => setWindowFocused(true)
    const onBlur = (): void => setWindowFocused(false)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    window.addEventListener('blur', onBlur)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('blur', onBlur)
    }
  }, [])

  const value = useMemo<ActiveChatValue>(
    () => ({ activeChatId, setActiveChatId, windowFocused }),
    [activeChatId, windowFocused],
  )

  return <ActiveChatContext.Provider value={value}>{children}</ActiveChatContext.Provider>
}

export function useActiveChat(): ActiveChatValue {
  const ctx = useContext(ActiveChatContext)
  if (ctx === null) {
    throw new Error('useActiveChat(): компонент должен быть внутри <ActiveChatProvider>')
  }
  return ctx
}
